// lessons/les1-motiverende-gespreksvoering/modules/Module2.js
export const MODULE2_CONFIG = {
  id: 'module2',
  title: 'Vragen Stellen: Verdiep het Gesprek',
  type: 'conversation',
  duration: 70,
  
  theory: {
    title: 'Vragen stellen: verdiep het gesprek',
    sections: [
      {
        id: 'open-vragen',
        title: 'Open vragen in motiverende gespreksvoering',
        content: `Wanneer je als zorgverlener een gesprek voert over gedragsverandering is het belangrijk om de binnenwereld van de cliënt te verkennen. Maar hoe laat je een cliënt nu praten over wat er in hem of haar omgaat? Een techniek die je kan toepassen is het stellen van open vragen. Open vragen hebben binnen motiverende gespreksvoering verschillende functies. In deze module richten we ons op het uitnodigen tot zelfonderzoek. Samen met de cliënt onderzoek je de motivatie om te veranderen, door bijbehorende gedachten en gevoelens te achterhalen. Dit helpt ook bij het aangaan van een samenwerking met de cliënt, en het focussen van het gesprek op wat voor de cliënt belangrijk is.`
      },
      {
        id: 'open-vs-gesloten',
        title: 'Open vs. gesloten vragen',
        content: `Open vragen beginnen vaak met woorden als hoe, wat, waar, wie, wanneer. Ze geven ruimte om gedachten en gevoelens te verkennen. Open vragen stimuleren reflectie en zelfonderzoek.

**Voorbeelden van open vragen zijn:**
"Hoe merkt u dat de klachten uw dagelijks leven beïnvloeden?"

"Wat betekent gezond eten voor u?"

"Wat maakt dit belangrijk voor u?"

Gesloten vragen sturen naar een kort antwoord (ja/nee of feit). Ze beperken de ruimte in het gesprek. Gesloten vragen kunnen functioneel zijn voor verduidelijking van eenvoudige zaken, maar ze zijn minder geschikt om motivatie of gevoelens te verkennen.

**Voorbeelden van gesloten vragen zijn:**
"Hebben de klachten invloed op uw dagelijks leven?"

"Eet u volgens een bepaald dieet?"

"Is dit belangrijk voor u?"

Vergelijk de open vragen met de gesloten vragen, en ga na wat voor antwoord de cliënt zou kunnen geven. Zo krijg je gevoel voor het verschil in gespreksdiepte dat elke vraag oproept. In de praktijk wissel je het stellen van open vragen af met andere technieken, zoals reflecteren. We komen daar later in deze cursus op terug.`
      },
              {
          id: 'schaalvragen',
          title: 'Schaalvragen',
          content: `Een schaalvraag is een open vraag waarin je de cliënt vraagt om iets op een schaal van 0 tot 10 te plaatsen. Bijvoorbeeld:

"Op een schaal van 0 tot 10, hoe belangrijk is deze verandering voor u?"

"Op een schaal van 0 tot 10, hoeveel vertrouwen heeft u dat u dit kunt?"

Het doel van schaalvragen is niet om een getal te krijgen, maar om een gesprek te openen over motivatie en vertrouwen. Het vervolg is cruciaal:

Als iemand bijvoorbeeld een 4 zegt, kun je vragen: "wat maakt dat het geen 2 of 3 is?" → dit lokt verandertaal uit, want de cliënt benoemt zelf redenen waarom het al hoger ligt.

Je kunt ook vragen: "Wat zou er nodig zijn om van een 4 naar een 5 te gaan?" → dit richt de aandacht op haalbare volgende stappen.

Schaalvragen geven structuur, terwijl ze toch ruimte laten voor de eigen woorden van de cliënt. Ze helpen om motivatie tastbaar te maken en maken kleine vooruitgang zichtbaar.`
        },
      {
        id: 'bronnen',
        title: 'Aanbevolen bronnen',
        content: `Wil jij meer weten over open vragen? Bestudeer dan onderstaande bronnen!

        Uitleg en oefeningen open vragen  
  https://www.motiverende-gespreksvoering.com/open-vragen/  
  Lees dit korte artikel over gesloten en open vragen en doe eventueel de oefeningen.
Leestijd: 4 minuten

Tijd winnen met gerichte open vragen
  https://www.youtube.com/watch?v=5aCfzC3YGDQ  
  Bekijk deze Engelstalige video over hoe je specifiekere vragen kan stellen om tijd te winnen in een consult.  
Kijktijd: 2 minuten

Schaalvragen gebruiken
https://www.youtube.com/watch?v=BpIBOijeXWw
Bekijk de video over hoe je schaalvragen kan gebruiken om verandertaal uit te lokken.
Kijktijd: 3 minuten
`},
{
          id: 'introductie opdracht',
          title: 'Introductie Opdracht',
          content: `**Uitleg**
          
        Je gaat straks in een chatgesprek oefenen met het stellen van open vragen.

Je eerste gesprek voer je met Sanne, waarbij jij de rol van haar oefentherapeut hebt. In het tweede gesprek spreek je met Yara en neem je de rol van haar huidtherapeut aan. Voorafgaand aan elk gesprek krijg je een korte beschrijving van de casus; deze kun je gebruiken als aanknopingspunt voor je vragen.

Je bent geslaagd wanneer je vijf keer een open vraag hebt gesteld die bijdraagt aan verdieping van het gesprek. Om te controleren of je de opdracht hebt behaald, klik je zelf op ‘Evalueer gesprek’. Deze evaluatie gebeurt niet automatisch.

**Tips**

Stel niet alleen vragen, maar houd het gesprek zo natuurlijk mogelijk.

Je hoeft het niet perfect te doen; zie het gesprek als een kans om te experimenteren en te leren.
          `
        }

    ]
  },

  conversations: {
    total: 2,
    passingCriteria: 'Tijdens een simulatiegesprek vijf open vragen stellen die de cliënt uitnodigen om gedachten, gevoelens of twijfels verder toe te lichten.',
    requiredOpenQuestions: 5,
    
    instruction: `Ga een natuurlijk gesprek aan met de cliënt, en stel open vragen om het gesprek te verdiepen. Als je vijf open vragen hebt gesteld, klik dan op 'Evalueer gesprek' om feedback te krijgen op je gesprekstechniek en na te gaan of je de opdracht hebt behaald.`,

    afterConversation1: `Goed gedaan! Je hebt vijf keer een goede open vraag gesteld waardoor het gesprek met de cliënt verdiepte. Lees nu onderstaande feedback op jouw gesprekstechnieken, en probeer nog effectiever vragen te stellen in de tweede ronde!

Tip: schrijf op welke feedback jij nuttig vindt om mee te nemen in het volgende simulatiegesprek!`,

    afterConversation2: `Goed gedaan! Je hebt nu opnieuw vijf keer een goede open vraag gesteld waardoor het gesprek met de cliënt verdiepte. Je hebt dus het leerdoel van deze module behaald. Lees onderstaande feedback en ga vervolgens verder met de volgende module over het stellen van specifieke vragen om verandertaal uit te lokken.

Tip: schrijf op welke feedback jij nuttig vindt om toe te passen in jouw praktijk als behandelaar!`
  },

  completionCriteria: {
    type: 'conversation_evaluation',

    requiredConversations: 2,
    description: 'Beide gesprekken succesvol afgerond met minimaal 5 open vragen per gesprek'
  }
};

export default MODULE2_CONFIG;